import { defineStore, acceptHMRUpdate } from 'pinia';
import keyBy from 'lodash/keyBy';
import { fetchFeatureFlags, toggleFeatureFlag, createFeatureFlag, updateFeatureFlag, deleteFeatureFlag } from '@/stores/api/feature-flags.api';

export interface FeatureFlag {
    flagName: string;
    [key: string]: unknown;
}

interface FeatureFlagsState {
    map: Record<string, FeatureFlag>;
    error: unknown;
    fetching: boolean;
}

export function getInitialState (): FeatureFlagsState {
    return {
        map: {},
        error: null,
        fetching: false,
    };
}

let loadAllPromise: Promise<void> | null = null;

export const useFeatureFlagsStore = defineStore('featureFlags', {
    state: getInitialState,
    actions: {
        setMap (flags: FeatureFlag[]) {
            this.map = keyBy(flags, 'flagName') as Record<string, FeatureFlag>;
        },
        setMapAtKey ({ id, flag }: { id: string; flag: FeatureFlag }) {
            this.map[id] = flag;
        },
        setError ({ error }: { error: unknown }) {
            this.error = error;
        },
        async loadAll ({ reFetch = false }: { reFetch?: boolean } = {}) {
            if (loadAllPromise && !reFetch) {
                await loadAllPromise;

                return;
            }

            this.fetching = true;
            loadAllPromise = fetchFeatureFlags().then((flags: FeatureFlag[]) => {
                this.setMap(flags);
                this.setError({ error: null });
                this.fetching = false;
            }).catch((error: unknown) => {
                loadAllPromise = null;
                this.setError({ error });
                this.fetching = false;
                throw error;
            });

            await loadAllPromise;
        },
        async createFeatureFlag ({ featureFlag }: { featureFlag: object }) {
            try {
                this.setError({ error: null });
                const flag = await createFeatureFlag(featureFlag);
                this.setMapAtKey({ id: flag.flagName, flag });

                return flag;
            } catch (error) {
                this.setError({ error });
                console.error(error);
                throw error;
            }
        },
        async toggle ({ flagName, enabled }: { flagName: string; enabled: boolean }) {
            const flag = await toggleFeatureFlag(flagName, enabled);
            this.setMapAtKey({ id: flagName, flag });
        },
        async updateFeatureFlag ({ featureFlag }: { featureFlag: object }) {
            try {
                this.setError({ error: null });
                const flag = await updateFeatureFlag(featureFlag);
                this.setMapAtKey({ id: flag.flagName, flag });
            } catch (error) {
                this.setError({ error });
                console.error(error);
                throw error;
            }
        },
        async deleteFeatureFlag ({ flagName }: { flagName: string }) {
            try {
                this.setError({ error: null });
                await deleteFeatureFlag(flagName);
                delete this.map[flagName];
            } catch (error) {
                this.setError({ error });
                console.error(error);
                throw error;
            }
        },
    },
    getters: {
        list: (state) => Object.values(state.map),
        featureFlagByName: (state) => (flagName: string) => state.map[flagName],
    },
});

export function __resetLoadAllPromiseForUnitTests () {
    loadAllPromise = null;
}

if (module.hot) {
    acceptHMRUpdate(useFeatureFlagsStore, module.hot);
}
